import { usePortalMessage, type PortalMessage } from "../../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * "Confirm it is you" — the step-up a security change asks for (plan-014 C2; design §18.15.2, §27.5.1). Owned by C2;
 * the Organization pages that change security or ownership use it.
 *
 * Three ways, chosen by the person: the password, the authenticator's six digits, or one recovery code
 * (`POST /v1/session/step-up` — `confirmIdentity`). So a person who signs in only with Google or Microsoft confirms
 * with their authenticator. A confirmation lasts a few minutes; the change that asked for it is retried once after it.
 *
 * GOOGLE OR MICROSOFT (ADR 0080 amendment, 2026-10-27; design §39.15): a person who holds neither a password nor an
 * authenticator confirms with the provider they sign in with, as on My security — one button per linked provider,
 * the browser goes there and comes back to this page (`returnPath`) with `?step_up=confirmed | cancelled | failed`,
 * which the page shows once (`stepUpReturnOutcome`) and drops from the address. Leaving the page drops the change, so
 * the person makes it again within the confirmation's few minutes.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import { Button, Dialog, Field, Input, Select } from "@wetalk/ui";
import { useState, type FormEvent, type ReactNode } from "react";

import { apiProblemText } from "../../../api/api-error.js";
import { useGateway } from "../../../api/provider.js";
import type { IdentityConfirmationFactor } from "../../../api/slice/account-security.js";
import { METHOD_WORD } from "../../personal-security/personal-security-copy.js";
import {
  PERSONAL_SECURITY_KEY,
  STEP_UP_RETURN,
  stepUpReturnToast,
  type StepUpReturn,
} from "../../personal-security/personal-security-screen.js";
import type { Outcome } from "../page/organization-page.js";

/** A change waiting for the confirmation, retried once it is given. */
export interface PendingConfirmation {
  readonly retry: () => Promise<void>;
}

/** Whether a refusal asks for a fresh confirmation. */
export function isStepUpRequired(error: unknown): boolean {
  return error instanceof ApiError && error.code === "step_up_required";
}

/** A page's `step_up` search parameter, read from the untrusted address bar (only the API's three words). */
export function stepUpReturnOf(raw: Readonly<Record<string, unknown>>): { readonly step_up?: StepUpReturn } {
  const stepUp = raw["step_up"];
  return (STEP_UP_RETURN as readonly unknown[]).includes(stepUp) ? { step_up: stepUp as StepUpReturn } : {};
}

/** The notice a Google or Microsoft confirmation's return shows on the page, once. */
export function stepUpReturnOutcome(word: StepUpReturn | undefined): Outcome | null {
  return stepUpReturnToast(word);
}

const FACTOR_WORD: Readonly<Record<IdentityConfirmationFactor, { readonly choice: string; readonly field: string; readonly wrong: string }>> = {
  get password() { return { get choice() { return portalText("core", "account_admin.my_password_d5cd66"); }, get field() { return portalText("core", "auth.password_8be3c9"); }, get wrong() { return portalText("core", "start.that_password_is_not_right_930b19"); } }; },
  get totp() { return { get choice() { return portalText("core", "account_admin.my_authenticator_app_8ba7df"); }, get field() { return portalText("core", "auth.6_digit_code_0c52ea"); }, get wrong() { return portalText("core", "auth.that_code_is_not_right_enter_the_6_digits_your_app_shows_now_5af97e"); } }; },
  get recovery_code() { return { get choice() { return portalText("core", "account_admin.a_recovery_code_b09d41"); }, get field() { return portalText("core", "auth.recovery_code_3cb5fd"); }, get wrong() { return portalText("core", "auth.that_recovery_code_is_not_right_or_it_was_used_already_916ef9"); } }; },
};

export function ConfirmIdentityDialog(props: {
  readonly pending: PendingConfirmation | null;
  /** Which way is offered first (the person's own: a password if they hold one, else the authenticator). */
  readonly preferred: IdentityConfirmationFactor;
  /** This page's path: where a Google or Microsoft confirmation brings the browser back. */
  readonly returnPath: string;
  readonly onDone: () => void;
  readonly onProblem: (text: PortalMessage) => void;
}): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const [factor, setFactor] = useState<IdentityConfirmationFactor>(props.preferred);
  const [secret, setSecret] = useState("");
  const [problem, setProblem] = usePortalMessage();
  const methods = useQuery({ queryKey: PERSONAL_SECURITY_KEY.methods, queryFn: () => gateway.readMySignInMethods(), enabled: props.pending !== null });
  // Neither a password nor an authenticator: the provider they sign in with confirms (the API re-checks all of it).
  const held = methods.data;
  const linkedProvider =
    held === undefined || held.password.enabled || held.totp.state === "active"
      ? []
      : (["google", "microsoft"] as const).filter((provider) => held[provider].linked);
  const confirmWithProvider = useMutation({
    mutationFn: async (provider: "google" | "microsoft") => {
      const started = await gateway.beginProviderStepUp(provider, props.returnPath);
      window.location.assign(started.startUrl);
    },
    onError: (raised: Error, provider) => {
      close();
      props.onProblem(() => (apiProblemText(raised, portalText("core", "personal_security.provider_step_up_could_not_start", { value0: METHOD_WORD[provider] }))));
    },
  });
  const close = (): void => {
    setSecret("");
    setProblem(null);
    props.onDone();
  };
  const confirm = useMutation({
    mutationFn: async () => {
      await gateway.confirmIdentity(factor, factor === "password" ? secret : secret.replace(/\s/g, ""));
      await props.pending?.retry();
    },
    onSuccess: close,
    onError: (raised: Error) => {
      if (raised instanceof ApiError && raised.code === "credential_invalid") {
        setProblem(() => (FACTOR_WORD[factor].wrong));
        return;
      }
      if (raised instanceof ApiError && raised.code === "validation_failed" && raised.field === "factor") {
        setProblem(() => (portalText("core", "account_admin.you_have_no_authenticator_app_set_up_choose_your_password_instead_ecdc31")));
        return;
      }
      close();
      props.onProblem(() => (apiProblemText(raised, portalText("core", "account_admin.that_change_could_not_be_made_0e975e"))));
    },
  });
  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (secret.trim() === "") return;
    confirm.mutate();
  };
  return (
    <Dialog open={props.pending !== null} title={portalText("core", "account_admin.confirm_it_is_you_e6fe51")} onClose={close}>
      {linkedProvider.length > 0 ? (
        <div className="sec-dialog-form">
          <p className="sec-dialog-text">{portalText("core", "personal_security.confirm_with_provider_lede")}</p>
          <div className="sec-dialog-actions">
            {linkedProvider.map((provider) => (
              <Button
                key={provider}
                variant="primary"
                disabled={confirmWithProvider.isPending}
                onClick={() => {
                  confirmWithProvider.mutate(provider);
                }}
              >
                {portalText("core", "personal_security.continue_with_provider_to_confirm", { value0: METHOD_WORD[provider] })}
              </Button>
            ))}
          </div>
        </div>
      ) : (
      <form className="sec-dialog-form" onSubmit={submit}>
        <p className="sec-dialog-text">{portalText("core", "account_admin.security_changes_ask_you_to_confirm_it_is_you_a_confirmation_last_bfd733")}</p>
        <Field label={portalText("core", "account_admin.confirm_with_7996ed")}>
          {(control) => (
            <Select
              {...control}
              value={factor}
              onChange={(event) => {
                setFactor(event.target.value as IdentityConfirmationFactor);
                setSecret("");
                setProblem(null);
              }}
            >
              {(Object.keys(FACTOR_WORD) as IdentityConfirmationFactor[]).map((one) => (
                <option key={one} value={one}>
                  {FACTOR_WORD[one].choice}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={FACTOR_WORD[factor].field} {...(problem === null ? {} : { error: problem })}>
          {(control) => (
            <Input
              {...control}
              type={factor === "password" ? "password" : "text"}
              inputMode={factor === "totp" ? "numeric" : undefined}
              autoComplete={factor === "password" ? "current-password" : "one-time-code"}
              value={secret}
              onChange={(event) => {
                setSecret(event.currentTarget.value);
              }}
            />
          )}
        </Field>
        <div className="sec-dialog-actions">
          <Button variant="primary" type="submit" disabled={confirm.isPending || secret.trim() === ""}>
            {confirm.isPending ? portalText("core", "start.confirming_0c2708") : portalText("core", "account_admin.confirm_04a212")}
          </Button>
        </div>
      </form>
      )}
    </Dialog>
  );
}
